import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import { Button, Divider, Stack } from "@mui/material";
import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router";
import { ReportAccessType } from "@entrilia/shared/api/reportAccessTypes.ts";
import ReportAuthorizedBox from "@entrilia/shared/components/ReportAuthorizedBox";
import { ReportType } from "../../../../../../api/types/svTypes.ts";
import { useGroupsContext } from "../../../report-lists/groups/contexts/GroupsContext.ts";
import { useAppDispatch } from "../../store/hooks/useAppDispatch";
import {
  selectCurrentReport,
  selectCurrentReportAuthorizationInfo,
} from "../../store/selectors/currentReportSelectors";
import { createCurrentReportAction } from "../../store/utils/storeUtils";
import { useUnsavedReportContext } from "../../contexts/UnsavedReportContext";
import ReportProductName from "../common/ReportProductName.tsx";
import ReportSelector from "../common/report-selector/ReportSelector";
import FinancialStatementViewerContainer from "../viewer/financialStatement/FinancialStatementViewerContainer.tsx";
import type { RunState } from "../viewer/storage/runState.ts";
import {
  isChartRunState,
  isFinancialStatementRunState,
  isListRunState,
  isPivotRunState,
  isTabularRunState,
  isTileRunState,
} from "../viewer/storage/runStateUtils.ts";
import ReportActionHeader from "./ReportActionHeader";
import ChartViewerContainer from "./chart/ChartViewerContainer";
import { ExportStateContextProvider } from "./common/contexts/ExportStateContextProvider";
import ExportAction from "./common/saving/ExportAction";
import MoreAction from "./common/saving/MoreAction";
import ShareReportButton from "./common/sharing/ShareReportButton";
import { DeveloperPanelWrapper } from "./dev/DeveloperPanelWrapper.tsx";
import ListViewerContainer from "./list/ListViewerContainer.tsx";
import PivotViewerContainer from "./pivot/PivotViewerContainer";
import TabularViewerContainer from "./tabular/TabularViewerContainer";
import TileViewerContainer from "./tile/TileViewerContainer";

interface Props {
  runState: RunState;
  getEditReportUrl: (clientCode: string, reportId: string) => string;
}

export default function ViewReportPage({ runState, getEditReportUrl }: Props) {
  const { groups } = useGroupsContext();
  const unsavedReport = useUnsavedReportContext();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const report = useSelector(selectCurrentReport);
  const authorizationInfo = useSelector(selectCurrentReportAuthorizationInfo);
  const [exportValuesAsDisplayed, setExportValuesAsDisplayed] = useState(false);
  const showExportAction = report?.reportType !== ReportType.Tile;

  useEffect(() => {
    dispatch(createCurrentReportAction("updateReportMode", false));
  }, [dispatch]);

  if (report === undefined) return <></>;
  return (
    <ExportStateContextProvider
      valueSettings={{ value: exportValuesAsDisplayed, onChange: setExportValuesAsDisplayed }}
    >
      <Stack direction="column" sx={{ flex: 1 }}>
        <ReportActionHeader
          ProductName={<ReportProductName title="Reporting" />}
          ReportSelector={<ReportSelector requiredAccess={ReportAccessType.Read} groups={groups} />}
        >
          <Stack direction={"row"} useFlexGap spacing={1} sx={{ height: 32 }}>
            {!unsavedReport && <ShareReportButton />}
            {showExportAction && <ExportAction />}
            {!unsavedReport && (
              <ReportAuthorizedBox
                permissions={["ManageReports"]}
                access={authorizationInfo?.access}
                requiredAccess={ReportAccessType.Write}
              >
                <Button
                  variant="outlined"
                  color="secondary"
                  startIcon={<EditOutlinedIcon />}
                  onClick={() => navigate(getEditReportUrl(report.clientCode, report.reportId))}
                >
                  Edit Report
                </Button>
                <MoreAction report={report} />
              </ReportAuthorizedBox>
            )}
          </Stack>
        </ReportActionHeader>
        <Divider />
        <Stack direction="row" sx={{ flex: 1 }}>
          {isTabularRunState(runState) && <TabularViewerContainer runState={runState} />}
          {isPivotRunState(runState) && <PivotViewerContainer runState={runState} />}
          {isChartRunState(runState) && <ChartViewerContainer runState={runState} />}
          {isTileRunState(runState) && <TileViewerContainer runState={runState} />}
          {isListRunState(runState) && <ListViewerContainer runState={runState} />}
          {isFinancialStatementRunState(runState) && <FinancialStatementViewerContainer runState={runState} />}
          <DeveloperPanelWrapper />
        </Stack>
      </Stack>
    </ExportStateContextProvider>
  );
}
